import React, { createContext, useContext, useReducer, useEffect, useRef } from "react";

const CartContext = createContext();

const CART_ACTIONS = {
  ADD_ITEM: "ADD_ITEM",
  REMOVE_ITEM: "REMOVE_ITEM",
  UPDATE_QUANTITY: "UPDATE_QUANTITY",
  CLEAR_CART: "CLEAR_CART",
  LOAD_CART: "LOAD_CART",
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const { product, quantity = 1 } = action.payload;
      const existingItemIndex = state.items.findIndex((item) => item.product._id === product._id);

      if (existingItemIndex >= 0) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
        };
        return {
          ...state,
          items: updatedItems,
        };
      } else {
        return {
          ...state,
          items: [...state.items, { product, quantity }],
        };
      }
    }

    case CART_ACTIONS.REMOVE_ITEM: {
      const { productId } = action.payload;
      return {
        ...state,
        items: state.items.filter((item) => !(item.product._id === productId)),
      };
    }

    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { productId, quantity } = action.payload;
      if (quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((item) => !(item.product._id === productId)),
        };
      }

      return {
        ...state,
        items: state.items.map((item) => (item.product._id === productId ? { ...item, quantity } : item)),
      };
    }

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: [],
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload || [],
      };

    default:
      return state;
  }
};

const initialState = {
  items: [],
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const hasLoadedLocalCart = useRef(false);
  const lastUpdatedRef = useRef(0);

  useEffect(() => {
    const savedCartRaw = localStorage.getItem("cart");
    if (savedCartRaw) {
      try {
        const parsed = JSON.parse(savedCartRaw);
        if (parsed && Array.isArray(parsed.items)) {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsed.items });
          lastUpdatedRef.current = parsed.lastUpdated || Date.now();
        } else if (Array.isArray(parsed)) {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsed });
          lastUpdatedRef.current = Date.now();
        }
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
      }
    }
    setTimeout(() => {
      hasLoadedLocalCart.current = true;
    }, 0);
  }, []);

  useEffect(() => {
    try {
      if (!hasLoadedLocalCart.current) {
        return;
      }

      const now = Date.now();
      lastUpdatedRef.current = lastUpdatedRef.current || now;
      const payload = {
        items: state.items,
        lastUpdated: now,
      };

      const existingRaw = localStorage.getItem("cart");
      if (existingRaw) {
        try {
          const existing = JSON.parse(existingRaw);
          const existingLast = existing?.lastUpdated || 0;
          if (existingLast > (lastUpdatedRef.current || 0)) {
            const existingItems = Array.isArray(existing.items) ? existing.items : existing;
            const ourItemsStr = JSON.stringify(state.items);
            const existingItemsStr = JSON.stringify(existingItems || []);
            if (ourItemsStr !== existingItemsStr) {
              dispatch({ type: CART_ACTIONS.LOAD_CART, payload: existingItems });
            }
            lastUpdatedRef.current = existingLast;
            return;
          }
        } catch (err) {}
      }

      const payloadStr = JSON.stringify(payload);
      if (existingRaw !== payloadStr) {
        localStorage.setItem("cart", payloadStr);
        lastUpdatedRef.current = now;
      }
    } catch (error) {
      console.error("Error saving cart to localStorage:", error);
    }
  }, [state.items]);

  useEffect(() => {
    const storageHandler = (e) => {
      if (e.key === "cart") {
        if (e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue);
            const existingItems = Array.isArray(parsed.items) ? parsed.items : Array.isArray(parsed) ? parsed : [];
            const existingLast = parsed.lastUpdated || 0;
            if (existingLast > (lastUpdatedRef.current || 0)) {
              dispatch({ type: CART_ACTIONS.LOAD_CART, payload: existingItems });
              lastUpdatedRef.current = existingLast;
            }
          } catch (err) {}
        }
      }
    };
    window.addEventListener("storage", storageHandler);
    return () => window.removeEventListener("storage", storageHandler);
  }, []);

  const addItem = (product, quantity = 1) => {
    dispatch({
      type: CART_ACTIONS.ADD_ITEM,
      payload: { product, quantity },
    });
    lastUpdatedRef.current = Date.now();
  };

  const removeItem = (productId) => {
    dispatch({
      type: CART_ACTIONS.REMOVE_ITEM,
      payload: { productId },
    });
    lastUpdatedRef.current = Date.now();
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { productId, quantity },
    });
    lastUpdatedRef.current = Date.now();
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    lastUpdatedRef.current = Date.now();
  };

  const clearCartTProfile = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    lastUpdatedRef.current = Date.now();
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      return total + item.product.price * item.quantity;
    }, 0);
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const isInCart = (productId) => {
    return state.items.some((item) => item.product._id === productId);
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find((item) => item.product._id === productId);
    return item ? item.quantity : 0;
  };

  const value = {
    items: state.items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getCartTotal,
    getItemCount,
    isInCart,
    getItemQuantity,
    clearCartTProfile,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
